import React, { useEffect, useState } from "react";
import { X, Globe, MapPin, Plus, UserPlus, Ban, Map as MapIcon, Loader2 } from "lucide-react";
import { externalEstablishmentsApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";

const CHIP = "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold";

export function ProspectDrawer({ prospect, onClose, selected, onToggleSelect, onConverted, onDismiss }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (prospect?.external_id) {
      externalEstablishmentsApi.get(prospect.external_id)
        .then((r) => active && setDetail(r.data))
        .catch(() => active && setDetail(null));
    } else { setDetail(null); }
    return () => { active = false; };
  }, [prospect]);

  if (!prospect) return null;
  const d = detail || {};
  const coordLat = prospect.lat ?? d.latitude;
  const coordLng = prospect.lng ?? d.longitude;

  const convert = async () => {
    setBusy(true);
    try {
      const r = await externalEstablishmentsApi.convert(prospect.external_id);
      toast.success(r.data.already_converted ? "Already a CRM lead" : "Converted to CRM lead");
      onConverted?.(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="prospect-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-testid="prospect-drawer-overlay" />
      <div className="relative z-10 h-full w-full max-w-sm overflow-y-auto bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`}><Globe className="mr-1 h-3 w-3" /> FSA</span>
            <h3 className="mt-2 text-lg font-bold text-slate-900" data-testid="prospect-drawer-name">{prospect.name || d.business_name || "Prospect"}</h3>
            <p className="mt-0.5 flex items-start gap-1 text-xs text-slate-500"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /> {d.address || prospect.post_code || "Address unavailable"}</p>
          </div>
          <button onClick={onClose} data-testid="prospect-drawer-close" className="rounded p-1 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {(prospect.fhrs_rating || d.fhrs_rating) && <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`}>FHRS {prospect.fhrs_rating || d.fhrs_rating}</span>}
          {prospect.best_fit && <span className={`${CHIP} border-teal-200 bg-teal-50 text-teal-700`}>{prospect.best_fit}</span>}
          <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}>{prospect.distance_km}km · {prospect.estimated_travel_min}min</span>
          <span className={`${CHIP} border-indigo-200 bg-indigo-50 text-indigo-700`}>{prospect.channel || "VISIT"}</span>
        </div>

        {(prospect.reasons || []).length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-slate-700">Why this prospect</p>
            {(prospect.reasons || []).map((r, i) => <p key={i} className="mt-1 text-[11px] text-slate-500">• {r}</p>)}
            {(prospect.similarity_reasons || []).map((r, i) => <p key={`s${i}`} className="mt-1 text-[11px] text-slate-400">• {r}</p>)}
          </div>
        )}

        <p className="mt-4 text-[11px] text-slate-400" data-testid="prospect-drawer-extid">external_establishment_id: {prospect.external_id}</p>

        <div className="mt-5 grid grid-cols-1 gap-2">
          <button data-testid="prospect-add-trip" onClick={onToggleSelect}
            className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold ${selected ? "bg-teal-100 text-teal-800" : "bg-teal-700 text-white hover:bg-teal-800"}`}>
            <Plus className="h-4 w-4" /> {selected ? "Added to this trip" : "Add to this trip"}
          </button>
          <button data-testid="prospect-convert" onClick={convert} disabled={busy || d.is_converted}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} {d.is_converted ? "Already a CRM lead" : "Convert to CRM Lead"}
          </button>
          <button data-testid="prospect-dismiss" onClick={onDismiss}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-rose-600 hover:bg-rose-50">
            <Ban className="h-4 w-4" /> Dismiss / exclude (7 days)
          </button>
          {(coordLat != null) && (
            <a data-testid="prospect-view-map" href={`https://www.google.com/maps/search/?api=1&query=${coordLat},${coordLng}`}
              target="_blank" rel="noreferrer"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-slate-500 hover:bg-slate-50">
              <MapIcon className="h-4 w-4" /> View on map
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
